import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import useInfoDialog from 'common/hooks/useInfoDialog';
import {
  savePartnerCarrierWebhookConfig,
  updatePartner,
  updatePartnerCatalogDefaults,
  updatePartnerProfileFilter,
  updatePartnerWithoutRefresh,
} from 'store/thunks/partnerThunks';
import PartnerCatalogConfigModal from './PartnerCatalogConfigModal';
import PartnerCatalogModal from './PartnerCatalogModal';
import PartnerCredsDialog from './PartnerCredsDialog';
import PartnerWebhookConfigModal from './PartnerWebhookConfigModal';

const PartnerActions = ({ partner }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showPartnerCreds, setShowPartnerCreds] = useState(false);
  const [partnerCreds, setPartnerCreds] = useState(undefined);
  const [showWebhookConfigModal, setShowWebhookConfigModal] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [showCatalogConfigModal, setShowCatalogConfigModal] = useState(false);
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const menuOpen = !!anchorEl;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const openWebhookConfigModal = () => setShowWebhookConfigModal(true);
  const closeCatalogModal = () => setShowCatalogModal(false);
  const openCatalogConfigModal = () => setShowCatalogConfigModal(true);

  const closeCatalogConfigModal = (_, reason) => {
    if (reason !== 'backdropClick') {
      setShowCatalogConfigModal(false);
    }
  };

  const closeWebhookConfigModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowWebhookConfigModal(false);
    }
  };

  const handleCarrierWebhookConfig = (webhookConfig) => {
    setLoading(true);

    dispatch(savePartnerCarrierWebhookConfig(webhookConfig))
      .then((response) => {
        if (response) {
          enqueueSnackbar('Partner carrier tracking webhook config updated successfully', {
            variant: 'success',
          });
          closeWebhookConfigModal();
        }
      })
      .finally(() => setLoading(false));
  };

  const handleCatalogConfig = (catalogConfig) => {
    setLoading(true);

    dispatch(updatePartnerCatalogDefaults(catalogConfig))
      .then((response) => {
        if (response) {
          enqueueSnackbar('Product availability updated.', { variant: 'success' });
          closeCatalogConfigModal();
        }
      })
      .finally(() => setLoading(false));
  };

  const closePartnerCredsDialog = () => {
    setShowPartnerCreds(false);
    setPartnerCreds(null);
    dispatch(updatePartnerProfileFilter({ filter: {} }));
  };

  const handleActivatePartner = () => {
    handleMenuClose();
    showInfoDialog({
      title: 'Confirm Activation',
      message: `Are you sure you want to activate ${partner.partnerName}?`,
      callback: () => {
        setLoading(true);
        dispatch(
          updatePartner({
            partnerUuid: partner.partnerUuid,
            data: {
              enabled: true,
            },
          })
        )
          .then((response) => {
            if (response) {
              enqueueSnackbar(`${partner.partnerName} has been activated successfully`, {
                variant: 'success',
              });
            }
          })
          .catch((error) => {
            enqueueSnackbar(`Failed to activate partner: ${error.message}`, { variant: 'error' });
          })
          .finally(() => {
            setLoading(false);
          });
      },
      cancelable: true,
      isWarning: false,
      confirmButtonText: 'Activate',
    });
  };

  const handleDeactivatePartner = () => {
    handleMenuClose();
    showInfoDialog({
      title: 'Confirm Deactivation',
      message: `Are you sure you want to deactivate ${partner.partnerName}?`,
      callback: () => {
        setLoading(true);
        dispatch(
          updatePartner({
            partnerUuid: partner.partnerUuid,
            data: {
              enabled: false,
            },
          })
        )
          .then((response) => {
            if (response) {
              enqueueSnackbar(`${partner.partnerName} has been deactivated successfully`, {
                variant: 'success',
              });
            }
          })
          .catch((error) => {
            enqueueSnackbar(`Failed to deactivate partner: ${error.message}`, { variant: 'error' });
          })
          .finally(() => {
            setLoading(false);
          });
      },
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Deactivate',
    });
  };

  const handleEnableApiAccess = () => {
    if (!partner.enabled) {
      showInfoDialog({
        title: 'Cannot Enable API Access',
        message:
          'Cannot enable API access for a disabled partner. Please enable the partner first.',
        callback: () => {},
        cancelable: false,
        confirmButtonText: 'OK',
        isWarning: true,
      });
      return;
    }

    handleMenuClose();
    showInfoDialog({
      title: 'Confirm Enable API Access',
      message: `Are you sure you want to enable API access for ${partner.partnerName}?`,
      callback: () => {
        setLoading(true);

        dispatch(
          updatePartnerWithoutRefresh({
            partnerUuid: partner.partnerUuid,
            data: {
              hasApiAccess: true,
            },
          })
        )
          .then((response) => {
            if (response) {
              enqueueSnackbar(`API access enabled successfully for ${partner.partnerName}`, {
                variant: 'success',
              });
              if (response.clientId) {
                setPartnerCreds(response);
                setShowPartnerCreds(true);
              }
            }
          })
          .finally(() => setLoading(false));
      },
      cancelable: true,
      isWarning: false,
      confirmButtonText: 'Enable',
    });
  };

  const handleDisableApiAccess = () => {
    handleMenuClose();
    showInfoDialog({
      title: 'Confirm Disable API Access',
      message: `Are you sure you want to disable API access for ${partner.partnerName}?`,
      callback: () => {
        setLoading(true);
        dispatch(
          updatePartner({
            partnerUuid: partner.partnerUuid,
            data: {
              hasApiAccess: false,
            },
          })
        )
          .then((response) => {
            if (response) {
              enqueueSnackbar(`API access disabled successfully for ${partner.partnerName}`, {
                variant: 'success',
              });
            }
          })
          .catch((error) => {
            enqueueSnackbar(`Failed to disable API access: ${error.message}`, { variant: 'error' });
          })
          .finally(() => {
            setLoading(false);
          });
      },
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Disable',
    });
  };

  return (
    <>
      <InfoDialog />

      <IconButton aria-label='Partner Actions' onClick={handleMenuOpen} disabled={loading}>
        {loading ? <CircularProgress size={20} /> : <MoreVertIcon />}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClick={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {partner.enabled ? (
          <MenuItem onClick={handleDeactivatePartner}>Deactivate</MenuItem>
        ) : (
          <MenuItem onClick={handleActivatePartner}>Activate</MenuItem>
        )}
        {partner.enabled &&
          (partner.hasApiAccess ? (
            <MenuItem onClick={handleDisableApiAccess}>Disable API Access</MenuItem>
          ) : (
            <MenuItem onClick={handleEnableApiAccess}>Enable API Access</MenuItem>
          ))}
        <MenuItem onClick={openWebhookConfigModal}>Edit Webhook Config</MenuItem>
        <MenuItem onClick={() => setShowCatalogModal(true)}>Manage Catalog</MenuItem>
        <MenuItem onClick={openCatalogConfigModal}>Edit Catalog Config</MenuItem>
      </Menu>

      {showCatalogModal && (
        <PartnerCatalogModal
          partner={partner}
          showModal={showCatalogModal}
          handleClose={closeCatalogModal}
        />
      )}

      {showWebhookConfigModal && (
        <PartnerWebhookConfigModal
          partner={partner}
          loading={loading}
          showModal={showWebhookConfigModal}
          handleClose={closeWebhookConfigModal}
          handleCarrierWebhookConfig={handleCarrierWebhookConfig}
        />
      )}

      {showCatalogConfigModal && (
        <PartnerCatalogConfigModal
          partner={partner}
          loading={loading}
          showModal={showCatalogConfigModal}
          handleClose={closeCatalogConfigModal}
          handleCatalogConfig={handleCatalogConfig}
        />
      )}

      {showPartnerCreds && partnerCreds && (
        <PartnerCredsDialog
          onCloseModal={closePartnerCredsDialog}
          partnerCreds={partnerCreds}
          showModal={showPartnerCreds}
        />
      )}
    </>
  );
};

export default PartnerActions;

PartnerActions.propTypes = {
  partner: PropTypes.shape({
    partnerUuid: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    hasApiAccess: PropTypes.bool.isRequired,
    newProductsActiveByDefault: PropTypes.bool,
  }).isRequired,
};
